import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/dag0itrep053ijub2170";

const TOPICS = ["提供选题线索", "投稿深读文章", "反馈建议", "媒体合作", "其他"];

export default function FeedbackForm() {
  const [topic, setTopic] = useState("提供选题线索");
  const [formError, setFormError] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("idle");
    setFormError("");

    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("contact_alt") || "").trim();
    const topicVal = String(fd.get("topic") || "");

    // 机器人陷阱：命中则给出与正常相同的反馈，不真正提交
    if (honeypot.length > 0) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    for (const [key, value] of fd.entries()) {
      if (key === "contact_alt") continue;
      payload.append(key, value as string);
    }
    if (topicVal) payload.set("topic", topicVal);

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const okCode = parsed?.code === "OK";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (res.ok && okCode && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(
          (isSpam
            ? "内容被识别为疑似垃圾信息，请稍后再试。"
            : serverMsg) || "提交失败，请稍后再试。"
        );
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，请稍后再试。");
      setStatus("error");
    }
  };

  return (
    <section id="feedback" className="bg-ink-950 py-14 text-paper-50 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-ember-400">投稿与反馈 FEEDBACK</p>
            <h2 className="font-display text-2xl font-black leading-snug md:text-4xl">
              一起把「瓜」<br />聊得更明白
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-paper-100/70">
              有好选题、行业观察，或对我们的内容有建议？欢迎留言。涉及具体人物的未经核实信息本站暂不采信，更多欢迎聚焦行业现象与观看方式的讨论。
            </p>
            <ul className="mt-6 flex flex-col gap-3 text-sm text-paper-100/75">
              <li className="flex items-center gap-3"><i className="ri-mail-send-line text-ember-400" /> 我们会定期查看每条留言</li>
              <li className="flex items-center gap-3"><i className="ri-shield-check-line text-ember-400" /> 您的联系方式仅用于答复，不会公开</li>
              <li className="flex items-center gap-3"><i className="ri-time-line text-ember-400" /> 通常 1-3 个工作日回复</li>
            </ul>
          </div>

          <form
            data-readdy-form="反馈投稿"
            onSubmit={handleSubmit}
            className="rounded-2xl border border-paper-50/10 bg-paper-50/[0.06] p-6 md:p-8"
          >
            <div className="flex flex-wrap gap-2">
              {TOPICS.map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setTopic(t)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition ${
                    topic === t
                      ? "bg-ember-600 text-paper-50"
                      : "bg-paper-50/10 text-paper-100/80 hover:bg-ember-600/40"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <input type="hidden" name="topic" value={topic} />

            {/* honeypot */}
            <div className="trap-field">
              <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs text-paper-100/70" htmlFor="name">怎么称呼您</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="您的称呼"
                  className="w-full rounded-lg border border-paper-50/15 bg-ink-900 px-4 py-2.5 text-sm text-paper-50 outline-none placeholder:text-paper-100/35 focus:border-ember-500"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs text-paper-100/70" htmlFor="email">联系邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-paper-50/15 bg-ink-900 px-4 py-2.5 text-sm text-paper-50 outline-none placeholder:text-paper-100/35 focus:border-ember-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-1.5 block text-xs text-paper-100/70" htmlFor="message">留言内容</label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="分享您的选题线索或建议…"
                className="w-full resize-none rounded-lg border border-paper-50/15 bg-ink-900 px-4 py-3 text-sm text-paper-50 outline-none placeholder:text-paper-100/35 focus:border-ember-500"
              />
              <p className="mt-1 text-right text-[11px] text-paper-100/40">最多 500 字</p>
            </div>

            {formError && (
              <p className="mt-3 flex items-start gap-2 rounded-lg bg-ember-800/40 px-4 py-2.5 text-sm text-ember-100">
                <i className="ri-error-warning-line mt-0.5" /> {formError}
              </p>
            )}
            {status === "success" && (
              <p className="mt-3 flex items-start gap-2 rounded-lg bg-amberstone-600/30 px-4 py-2.5 text-sm text-amberstone-100">
                <i className="ri-checkbox-circle-line mt-0.5" /> 已收到您的留言，感谢反馈！
              </p>
            )}

            <button
              type="submit"
              className="mt-5 inline-flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-ember-600 px-6 py-3.5 text-sm font-semibold text-paper-50 transition hover:bg-ember-500 sm:w-auto"
            >
              提交留言 <i className="ri-send-plane-fill ml-2" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}